import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DsCheckboxComponent } from '@design-system/ds-checkbox/component/ds-checkbox.component';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsSelectComponent } from '@design-system/ds-select/component/ds-select.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { DsTextareaComponent } from '@design-system/ds-textarea/component/ds-textarea.component';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { merge, startWith, Subject, takeUntil } from 'rxjs';
import { calculateRateAndFlightMargin } from '../../../helpers/calculate-rate-and-flight-margin.helper';
import { compareDropdownById } from '../../../helpers/compare-dropdown-by-id.helper';
import { isFilledValue } from '../../../helpers/is-filled-value.helper';
import {
  BLOCK_TIME_BILLED_HOURS_INFO,
  BLOCK_TIME_EMPTY_VALUE,
  BLOCK_TIME_FIELD_ERRORS,
  BLOCK_TIME_HOURS_ERROR,
  BLOCK_TIME_NOTE_MAX_LENGTH,
} from '../../../properties/block-time-record-modal.properties';
import { BlockTimeBillingFormGroup } from '../../../types/block-time-record-form.type';

/**
 * Shows the billing fields of a block time record.
 * The billed hours come first, next to the actual and the estimated totals that the user compares them with.
 */
@Component({
  selector: 'app-btr-billing-info',
  templateUrl: './btr-billing-info.component.html',
  styleUrls: ['./btr-billing-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    MatFormFieldModule,
    DsCheckboxComponent,
    DsInputComponent,
    DsSelectComponent,
    DsTextComponent,
    DsTextareaComponent,
  ],
})
export class BtrBillingInfoComponent implements OnChanges, OnDestroy {
  @Input() group: BlockTimeBillingFormGroup | null = null;
  @Input() isAddMode = false;
  @Input() isContracted = false;
  /** The saved rate. The view shows it until the user changes the cost or the profit. */
  @Input() storedRate: number | null = null;
  /** The saved flight margin. The view shows it until the user changes the cost or the profit. */
  @Input() storedFlightMargin: number | null = null;
  @Input() monthYearOptions: BaseDropdownInterface[] = [];
  /** The actual block time, for example '11.68'. */
  @Input() actualTotal = BLOCK_TIME_EMPTY_VALUE;
  /** The estimated block hours, for example '6.82'. */
  @Input() estimatedTotal = BLOCK_TIME_EMPTY_VALUE;
  /** The modal increments this value after an invalid save, so that this OnPush view shows the field errors. */
  @Input() validationRun = 0;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly groupChange$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  readonly billedHoursInfo = BLOCK_TIME_BILLED_HOURS_INFO;
  readonly hoursError = BLOCK_TIME_HOURS_ERROR;
  readonly monthYearError = BLOCK_TIME_FIELD_ERRORS.monthYear;
  readonly noteMaxLength = BLOCK_TIME_NOTE_MAX_LENGTH;
  readonly compareById = compareDropdownById;
  readonly contractedControl = new FormControl<boolean>({ value: false, disabled: true }, { nonNullable: true });

  rate = BLOCK_TIME_EMPTY_VALUE;
  flightMargin = BLOCK_TIME_EMPTY_VALUE;
  totalsText = '';
  noteCounter = `0 / ${BLOCK_TIME_NOTE_MAX_LENGTH}`;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isContracted']) {
      this.contractedControl.setValue(this.isContracted);
    }

    if (changes['actualTotal'] || changes['estimatedTotal']) {
      this.totalsText = `Actual ${this.actualTotal} hrs · Estimated ${this.estimatedTotal} hrs`;
    }

    if (changes['group']) {
      this.watchRateAndMargin();
    }
  }

  ngOnDestroy(): void {
    this.groupChange$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private watchRateAndMargin(): void {
    this.groupChange$.next();

    if (!this.group) {
      return;
    }

    const { cost, profit, note } = this.group.controls;

    note.valueChanges.pipe(startWith(note.value), takeUntil(this.groupChange$), takeUntil(this.destroy$)).subscribe((value) => {
      this.noteCounter = `${value?.length ?? 0} / ${BLOCK_TIME_NOTE_MAX_LENGTH}`;
      this.cdr.markForCheck();
    });

    merge(cost.valueChanges, profit.valueChanges)
      .pipe(startWith(null), takeUntil(this.groupChange$), takeUntil(this.destroy$))
      .subscribe(() => {
        const hasValues = isFilledValue(cost.value) && isFilledValue(profit.value);
        const isChanged = cost.dirty || profit.dirty;
        const result =
          isChanged || !isFilledValue(this.storedRate)
            ? calculateRateAndFlightMargin(cost.value, profit.value)
            : { rate: Number(this.storedRate), flightMargin: Number(this.storedFlightMargin) };

        // Without a cost and a profit there is no rate, so the view shows the empty text instead of 0.00.
        this.rate = hasValues ? `${result.rate.toFixed(2)} %` : BLOCK_TIME_EMPTY_VALUE;
        this.flightMargin = hasValues ? `${result.flightMargin.toFixed(2)} %` : BLOCK_TIME_EMPTY_VALUE;
        this.cdr.markForCheck();
      });
  }
}
